import { baBsFormSchema, openItemDirectionSchema } from "@station/contracts";
import { addDays, type LocalDate } from "@station/core";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { startOfYear, todayIstanbul } from "../../../features/accounting/shared.js";
import { LettersPanel } from "../../../features/counterparties/letters-panel.js";
import { AgingReport } from "../../../features/counterparties/reports/aging.js";
import { BaBsReport } from "../../../features/counterparties/reports/ba-bs.js";
import { DueCalendarReport } from "../../../features/counterparties/reports/due-calendar.js";
import { LedgerReconciliationReport } from "../../../features/counterparties/reports/ledger-reconciliation.js";
import { StatementReport } from "../../../features/counterparties/reports/statement.js";
import type {
  CounterpartyReportProps,
  CounterpartyReportSearch,
} from "../../../features/counterparties/reports/types.js";
import { CounterpartyPage } from "../../../features/counterparties/shared.js";

const REPORTS = ["statement", "aging", "due-calendar", "ba-bs", "letters", "ledger-reconciliation"] as const;
type ReportKey = (typeof REPORTS)[number];

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(REPORTS).optional().catch(undefined),
  counterparty: z.string().uuid().optional().catch(undefined),
  from: date,
  to: date,
  asOf: date,
  direction: openItemDirectionSchema.optional().catch(undefined),
  boundaries: z
    .string()
    .regex(/^\d{1,4}(,\d{1,4}){0,5}$/)
    .optional()
    .catch(undefined),
  form: baBsFormSchema.optional().catch(undefined),
  year: z.coerce.number().int().min(2000).max(2999).optional().catch(undefined),
  month: z.coerce.number().int().min(1).max(12).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/cari/raporlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CounterpartyReportsPage,
});

function Letters({ companyId }: CounterpartyReportProps): ReactElement {
  return <LettersPanel companyId={companyId} />;
}

const COMPONENTS: Record<ReportKey, (props: CounterpartyReportProps) => ReactElement> = {
  statement: StatementReport,
  aging: AgingReport,
  "due-calendar": DueCalendarReport,
  "ba-bs": BaBsReport,
  letters: Letters,
  "ledger-reconciliation": LedgerReconciliationReport,
};

function CounterpartyReportsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const tab = search.tab ?? "statement";
  const today = todayIstanbul();
  // Ekstre yıl başından bugüne; vade takvimi bugünden 30 gün ileriye bakar.
  const range =
    tab === "due-calendar"
      ? { from: search.from ?? today, to: search.to ?? addDays((search.from ?? today) as LocalDate, 30) }
      : { from: search.from ?? startOfYear(search.to ?? today), to: search.to ?? today };
  const setSearch = (patch: Partial<CounterpartyReportSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(
          Object.entries({ ...previous, ...patch }).filter(
            ([, value]) => value !== undefined && value !== "",
          ),
        ),
      replace: true,
    });

  return (
    <CounterpartyPage
      title={t("menu.counterpartyReports")}
      description={t("counterparties.reports.description")}
    >
      {({ companyId }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({
              // Sekme değişince yalnızca şirket ve seçili cari korunur; tarih aralığının anlamı rapora göre değişir.
              search: (previous) => ({
                company: previous.company,
                counterparty: previous.counterparty,
                tab: next as ReportKey,
              }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("menu.counterpartyReports")} className="flex-wrap">
            {REPORTS.map((report) => (
              <TabsTrigger key={report} value={report}>
                {t(`counterparties.reports.tabs.${report}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {REPORTS.map((report) => {
            const Component = COMPONENTS[report];
            return (
              <TabsContent key={report} value={report}>
                {tab === report ? (
                  <Component
                    companyId={companyId}
                    search={search}
                    setSearch={setSearch}
                    range={range}
                    today={today}
                  />
                ) : null}
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </CounterpartyPage>
  );
}
